import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface Guest {
  id: string;
  name: string;
  phone: string;
  guests_count: number;
  message: string | null;
  created_at: string;
}

interface UseGuestManagementReturn {
  guests: Guest[];
  loading: boolean;
  error: string | null;
  editGuest: (guestId: string, data: Partial<Guest>) => Promise<void>;
  deleteGuest: (guestId: string) => Promise<void>;
  refreshGuests: () => Promise<void>;
}

export function useGuestManagement(): UseGuestManagementReturn {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGuests = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('guests')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (fetchError) throw fetchError;
      setGuests(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching guests:', err);
      setError('Failed to fetch guests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuests();
  }, []);

  const editGuest = async (guestId: string, data: Partial<Guest>) => {
    try {
      const { error: updateError } = await supabase
        .from('guests')
        .update({
          name: data.name,
          phone: data.phone,
          guests_count: data.guests_count,
          message: data.message || null
        })
        .eq('id', guestId);
        
      if (updateError) throw updateError;
      
      setGuests(guests.map(guest => 
        guest.id === guestId 
          ? { ...guest, ...data }
          : guest
      ));
      
      setError(null);
    } catch (err) {
      console.error('Error updating guest:', err);
      throw new Error('Failed to update guest');
    }
  };

  const deleteGuest = async (guestId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('guests')
        .delete()
        .eq('id', guestId);
        
      if (deleteError) throw deleteError;
      
      setGuests(guests.filter(guest => guest.id !== guestId));
      setError(null);
    } catch (err) {
      console.error('Error deleting guest:', err);
      throw new Error('Failed to delete guest');
    }
  };

  return {
    guests,
    loading,
    error,
    editGuest,
    deleteGuest,
    refreshGuests: fetchGuests
  };
}